import { useState, type FunctionComponent } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Popconfirm, Space, Table, Tag, Typography, message } from "antd";

import {
  HabitType,
  type HabitCycle,
  type HabitSchedule,
  type HabitTemplateEntity,
  type HabitTime,
} from "@fitness-app/data-models/entities/Habit";

import { supabase } from "~/store/initializeStore";
import { asHabitColor, habitColorHex, habitTypeColors, habitTypeLabels } from "./constants";
import { HabitIcon } from "./habitIcons";
import HabitAssignModal from "./HabitAssignModal";
import HabitEnrollmentDrawer from "./HabitEnrollmentDrawer";
import HabitFormDrawer from "./HabitFormDrawer";
import { formatCycle, formatSchedule, formatTime } from "./helpers";

type HabitListRow = HabitTemplateEntity & {
  clients: { count: number }[];
};

const HabitsListPage: FunctionComponent = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingHabit, setEditingHabit] = useState<HabitTemplateEntity | null>(null);
  const [assigningHabit, setAssigningHabit] = useState<HabitTemplateEntity | null>(null);
  // Read-only enrollment/ranking preview per template.
  const [enrollmentHabit, setEnrollmentHabit] = useState<HabitTemplateEntity | null>(null);

  const habitsQuery = useQuery({
    queryKey: ["habits-list"],
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("habit_template")
        .select("*, clients:client_habit(count)")
        .eq("archived", false)
        .order("createdAt", { ascending: false })
        .returns<HabitListRow[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  const deleteHabitMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("habit_template").delete().eq("id", id);

      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void message.success("Usunięto");
      void queryClient.invalidateQueries({ queryKey: ["habits-list"] });
    },
    onError: (error: Error) => {
      void message.error(`Nie udało się usunąć: ${error.message}`);
    },
  });

  const openCreate = () => {
    setEditingHabit(null);
    setIsFormOpen(true);
  };

  const openEdit = (habit: HabitTemplateEntity) => {
    setEditingHabit(habit);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingHabit(null);
  };

  return (
    <Card
      title="Nawyki i zadania"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Nowy nawyk / zadanie
        </Button>
      }
    >
      <Table<HabitListRow>
        dataSource={habitsQuery.data ?? []}
        rowKey="id"
        size="middle"
        scroll={{ x: true }}
        loading={habitsQuery.isFetching}
        locale={{ emptyText: "Brak nawyków i zadań" }}
        pagination={false}
      >
        <Table.Column<HabitListRow>
          title="Nazwa"
          dataIndex="name"
          key="name"
          render={(name, row) => (
            <Space direction="vertical" size={0}>
              <Space size={8}>
                <HabitIcon iconKey={row.icon} size={16} color={habitColorHex[asHabitColor(row.color)]} />
                <Typography.Text strong>{name}</Typography.Text>
              </Space>
              {row.description ? (
                <Typography.Text type="secondary" ellipsis style={{ maxWidth: 320 }}>
                  {row.description}
                </Typography.Text>
              ) : null}
            </Space>
          )}
        />
        <Table.Column<HabitListRow>
          title="Typ"
          dataIndex="type"
          key="type"
          render={(type: HabitType) => (
            <Tag color={habitTypeColors[type] ?? "default"}>{habitTypeLabels[type] ?? type}</Tag>
          )}
        />
        <Table.Column<HabitListRow>
          title="Harmonogram"
          key="schedule"
          render={(_, row) => formatSchedule(row.schedule as HabitSchedule)}
        />
        <Table.Column<HabitListRow>
          title="Cykl"
          key="cycle"
          // TRE-1656 — tasks carry a date window instead of a cycle.
          render={(_, row) => (row.type === HabitType.Task ? "—" : formatCycle(row.cycle as HabitCycle))}
        />
        <Table.Column<HabitListRow> title="Pora" key="time" render={(_, row) => formatTime(row.time as HabitTime)} />
        <Table.Column<HabitListRow>
          title="Przypisani"
          key="clients"
          width={110}
          render={(_, row) => (
            <Button type="link" className="px-0" onClick={() => setEnrollmentHabit(row)}>
              {row.clients?.[0]?.count ?? 0}
            </Button>
          )}
        />
        <Table.Column<HabitListRow>
          title="Opcje"
          key="action"
          render={(_, row) => (
            <Space size="middle">
              <Button type="link" className="px-0" onClick={() => setEnrollmentHabit(row)}>
                Zapisani
              </Button>
              <Button type="link" className="px-0" onClick={() => openEdit(row)}>
                Edytuj
              </Button>
              <Button type="link" className="px-0" onClick={() => setAssigningHabit(row)}>
                Przypisz
              </Button>
              <Popconfirm
                title="Usunąć nawyk / zadanie?"
                description="Usunięcie szablonu usunie też przypisania klientek."
                okText="Usuń"
                cancelText="Anuluj"
                okButtonProps={{ danger: true, loading: deleteHabitMutation.isLoading }}
                onConfirm={() => deleteHabitMutation.mutate(row.id)}
              >
                <Button type="link" danger className="px-0">
                  Usuń
                </Button>
              </Popconfirm>
            </Space>
          )}
        />
      </Table>

      <HabitFormDrawer open={isFormOpen} habit={editingHabit} onClose={closeForm} onSaved={closeForm} />
      <HabitAssignModal open={Boolean(assigningHabit)} habit={assigningHabit} onClose={() => setAssigningHabit(null)} />
      <HabitEnrollmentDrawer
        open={Boolean(enrollmentHabit)}
        habit={enrollmentHabit}
        onClose={() => setEnrollmentHabit(null)}
      />
    </Card>
  );
};

export default HabitsListPage;
